import { logAmplitudeEventAllowCustomers } from 'amplitude/amplitude'
import { Alert, ComponentVersions_3_0, ComponentVersionsInherit, styled } from 'opensolar-ui'
import { logCheckoutV2ErrorEncountered, logOptimizedCheckoutEvent } from 'myenergy/dialogs/checkout/cashFlow/checkout_2/utils'
import { LenderType } from 'opensolar-ui/src/checkout/checkoutFinancing/types'
import { useNotify, useTranslate } from 'ra-core'
import { useCallback, useEffect, useState } from 'react'
import restClient from 'restClient'
import { getBool, setBool } from 'storage/appStorage'
import { ExpectedMilestonePaymentType } from 'types/paymentOptions'
import { getExpectedMilestones } from 'util/cashFlow'
import { currencySymbolForCountry } from 'util/misc'
import CheckoutFinancingDialogUi from './CheckoutFinancingDialogUI'
import { ExitSurveyConfig } from './financeMarketplace/FinanceMarketplaceDialogContent'
import FinanceMarketplaceExitSurveyDialog from './financeMarketplace/FinanceMarketplaceExitSurveyDialog'
import InitialLoadingDialog from './InitialLoadingDialog'
import { PreviewModeContext, StandaloneModeContext } from './previewMode'
import { CheckoutFinancingPricingParams } from './types'
import {
  useGetAvailableCheckoutFinanceLenders,
  useGetCheckoutFinancePricingOptions,
  useGetInitialMarketplaceFilterParameters,
} from './utils'

const restClientInstance = restClient(window.API_ROOT + '/api')

type PropTypes = {
  open: boolean
  onClose: () => void
  orgId: number
  projectId: number
  countryIso2: string
  systemUuid: string
  paymentOptionId: number
  proposalUrl?: string
  expectedMilestonePayments?: ExpectedMilestonePaymentType[] | undefined
  amountDue: number
  standalone?: boolean
  // When true, the dialog is being previewed by a Pro (via the "Test" button in the share popover).
  // Applications must not be submitted and a warning banner is shown at the top.
  preview?: boolean
  onSuccess?: () => void | Promise<void>
  fromOptimizedCheckout?: boolean
}

const PreviewBannerWrapper = styled('div')({
  maxWidth: 900,
  margin: '0 auto',
  padding: '16px 24px 0 24px',
})

const CheckoutFinancingDialog = (props: PropTypes) => {
  const {
    open,
    onClose,
    proposalUrl,
    projectId,
    systemUuid,
    paymentOptionId,
    orgId,
    expectedMilestonePayments: expectedMilestonePaymentsProp,
    amountDue,
    countryIso2,
    standalone = false,
    preview = false,
    onSuccess,
    fromOptimizedCheckout = false,
  } = props
  const notify = useNotify()
  const translate = useTranslate()
  const currencySymbol = currencySymbolForCountry(countryIso2)

  const [loanAmount, setLoanAmount] = useState(amountDue)
  const [expectedMilestonePayments, setExpectedMilestonePayments] = useState<ExpectedMilestonePaymentType[]>([])
  const [pricingParams, setPricingParams] = useState<CheckoutFinancingPricingParams | null>(null)
  const [exitSurveyConfig, setExitSurveyConfig] = useState<ExitSurveyConfig | null>(null)
  const [applicationStarted, setApplicationStarted] = useState(false)
  const [showExitSurveyDialog, setShowExitSurveyDialog] = useState(false)

  const {
    isLoading: lendersAreLoading,
    lenders,
    marketPlaceLenders,
    referralLenders,
  } = useGetAvailableCheckoutFinanceLenders(orgId, projectId)
  // get the initial filter parameters so we can populated pricingParams with default values like
  // the min/max term and min deposit
  const { isLoading: initialFiltersAreLoading, filterParameters: initialFilterParameters } =
    useGetInitialMarketplaceFilterParameters(orgId, projectId, systemUuid, paymentOptionId)

  const {
    isLoading: pricingOptionsLoading,
    pricingOptions,
    filterParameters,
    totalPricingOptions,
  } = useGetCheckoutFinancePricingOptions(orgId, projectId, pricingParams)

  useEffect(() => {
    if (!pricingParams && initialFilterParameters) {
      setPricingParams({
        org_id: orgId,
        project_id: projectId,
        system_uuid: systemUuid,
        upfront_payment: initialFilterParameters.minimum_upfront_payment,
        term_start: initialFilterParameters.minimum_term,
        term_end: initialFilterParameters.maximum_term,
        monthly_repayment_start: initialFilterParameters.minimum_monthly_repayment,
        monthly_repayment_end: initialFilterParameters.maximum_monthly_repayment,
        filters: {
          product_type: null,
          sorting: 'lowest_monthly_payment',
        },
        payment_option_id: paymentOptionId,
        show_valid_only: true,
        page: 1,
        limit: 5,
      })
    }
  }, [initialFilterParameters, pricingParams, orgId, projectId, systemUuid, paymentOptionId])

  const updatePricingParams = (newParams: CheckoutFinancingPricingParams) => {
    setPricingParams(newParams)
  }

  const fetchExpectedMilestonePayments = useCallback(() => {
    if (orgId && systemUuid && paymentOptionId) {
      if (expectedMilestonePaymentsProp) {
        setExpectedMilestonePayments(expectedMilestonePaymentsProp)
      } else {
        // Fetch expected milestones if no expected milestones are provided
        getExpectedMilestones(orgId, projectId, systemUuid, paymentOptionId).then((res) => {
          setExpectedMilestonePayments(res.data.expected_milestone_payments)
          setLoanAmount(res.data.total_amount_payable - res.data.total_amount_paid)
        })
      }
    }
  }, [orgId, projectId, systemUuid, paymentOptionId, expectedMilestonePaymentsProp])

  useEffect(() => {
    if (orgId && projectId && systemUuid && paymentOptionId) {
      fetchExpectedMilestonePayments()
    }
  }, [orgId, projectId, systemUuid, paymentOptionId])

  useEffect(() => {
    if (!open) {
      setApplicationStarted(false)
      setShowExitSurveyDialog(false)
    }
  }, [open])

  const handleApplyNowButton = async (lender: LenderType): Promise<void> => {
    if (preview) {
      notify(
        translate('Preview mode: applications are disabled. Share this link with your customer so they can apply.'),
        'warning'
      )
      return
    }
    return new Promise((resolve, reject) => {
      if (loanAmount === 0) {
        notify('Please edit the loan amount or select milestones to proceed.')
        resolve()
      }

      restClientInstance('CUSTOM_POST', 'custom', {
        url: `orgs/${orgId}/projects/${projectId}/checkout_financing/affiliate_link/?finance_cta_id=${lender.cta_id}`,
        data: { loanAmount, paymentOptionId, systemUuid },
      })
        .then((response) => {
          const link = document.createElement('a')
          link.href = response.data.affiliateLink
          link.target = '_blank'
          link.rel = 'noopener noreferrer sponsored'
          link.style.display = 'none'
          document.body.appendChild(link)
          link.click()
          document.body.removeChild(link)

          const hasNotes = response.data.affiliateLink?.includes('notes=')

          logAmplitudeEventAllowCustomers('checkout_finance_dialog_apply_clicked', {
            org_id: orgId,
            project_id: projectId,
            lender: lender.integration_name,
            has_notes: hasNotes,
          })
          if (fromOptimizedCheckout) {
            logOptimizedCheckoutEvent('checkout_v2_finance_marketplace_apply_now', {
              lender_name: lender.integration_name,
              loan_offering_name: '',
            })
          }
        })
        .catch((err) => {
          if (err?.body?.message) {
            notify(err?.body?.message, 'warning')
          } else {
            notify('Failed to launch loan application url', 'warning')
          }
          if (fromOptimizedCheckout) {
            logCheckoutV2ErrorEncountered({
              step_context: 'explore_finance',
              err,
              error_message: 'Failed to launch loan application url',
            })
          }
        })
        .finally(() => {
          resolve()
        })
    })
  }

  const handleEditLoanAmount = (field) => {
    logAmplitudeEventAllowCustomers('checkout_finance_dialog_loan_amount_edited', {
      org_id: orgId,
      project_id: projectId,
      edited_field: field,
    })
  }

  const handleCloseWithSuccess = () => {
    if (applicationStarted) {
      void onSuccess?.()
    }
    onClose()
  }

  const handleCloseRequest = () => {
    if (
      exitSurveyConfig?.should_see_dialog &&
      !applicationStarted &&
      !getBool('checkout_finance_dialog_exit_survey_shown')
    ) {
      setShowExitSurveyDialog(true)
      setBool('checkout_finance_dialog_exit_survey_shown', true)
    } else {
      handleCloseWithSuccess()
    }
  }

  const handleExitSurveyDismiss = () => {
    setShowExitSurveyDialog(false)
    handleCloseWithSuccess()
  }

  if (!pricingParams && initialFiltersAreLoading) {
    if (standalone) {
      return (
        <ComponentVersionsInherit versions={ComponentVersions_3_0}>
          <div style={{ padding: 24, textAlign: 'center' }}>{translate('Loading...')}</div>
        </ComponentVersionsInherit>
      )
    }
    return <InitialLoadingDialog />
  }
  if (pricingParams) {
    return (
      <ComponentVersionsInherit versions={ComponentVersions_3_0}>
        <PreviewModeContext.Provider value={preview}>
          <StandaloneModeContext.Provider value={standalone}>
            {preview && (
              <PreviewBannerWrapper>
                <Alert severity="warning">
                  {translate(
                    'Preview mode: you are viewing this page as a Pro. Applications are disabled — share this link with your customer for them to apply.'
                  )}
                </Alert>
              </PreviewBannerWrapper>
            )}
            <CheckoutFinancingDialogUi
              open={open}
              onClose={handleCloseRequest}
              standalone={standalone}
              fromOptimizedCheckout={fromOptimizedCheckout}
              onExitSurveyConfigLoaded={setExitSurveyConfig}
              onApplicationStarted={() => setApplicationStarted(true)}
              actionData={{
                org_id: orgId,
                project_id: projectId,
                payment_option_id: paymentOptionId,
                system_uuid: systemUuid,
                pricing: {
                  system_price_including_tax: loanAmount,
                },
              }}
              onApply={handleApplyNowButton}
              onEditLoanAmount={handleEditLoanAmount}
              currencySymbol={currencySymbol}
              expectedMilestonePayments={expectedMilestonePayments}
              isLoading={lendersAreLoading}
              lenders={lenders}
              marketPlaceLenders={marketPlaceLenders}
              referralLenders={referralLenders}
              translate={translate}
              pricingOptions={pricingOptions}
              pricingOptionsLoading={pricingOptionsLoading}
              updatePricingParams={updatePricingParams}
              pricingParams={pricingParams}
              filterParameters={filterParameters}
              totalPricingOptions={totalPricingOptions}
              proposalUrl={proposalUrl}
            />
            <FinanceMarketplaceExitSurveyDialog
              open={showExitSurveyDialog}
              question={exitSurveyConfig?.question ?? ''}
              answers={exitSurveyConfig?.answers ?? []}
              onDismiss={handleExitSurveyDismiss}
              translate={translate}
            />
          </StandaloneModeContext.Provider>
        </PreviewModeContext.Provider>
      </ComponentVersionsInherit>
    )
  }
  return null
}

export default CheckoutFinancingDialog
